import Chart from "react-apexcharts";
import ApexCharts from "apexcharts";
import SelectDropdown from "../../shared/components/SelectDropdown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar, 
  faTrash, 
  faLightbulb,
  faExclamationCircle,
  faTimes,
  faDownload
} from "@fortawesome/free-solid-svg-icons";

const CATEGORY_ORDER = [
  "Dates & Times",
  "People & Entities",
  "Locations (Origin)",
  "Locations (Destination)",
  "Shipment Details",
  "Status & Tracking",
  "Dimensions & Weight",
  "Costs & Pricing",
  "References",
  "Other",
];

const VisualizationBuilder = ({
  vizTableOptions,
  vizLeftTable,
  setVizLeftTable,
  vizRightTable,
  setVizRightTable,
  vizJoinOptions,
  vizJoinKey,
  setVizJoinKey,
  vizChartOptions,
  vizChartType,
  setVizChartType,
  vizAggregate,
  setVizAggregate,
  vizAggregateOptions = [],
  clearVizMapping,
  visualizationTables = [],
  vizTableToggles = {},
  setVizTableToggles = () => {},
  isVizDataLoading,
  vizDataError,
  availableColumns,
  showTableSelectors = true,
  xCategoryOptions = [],
  xCategoryFilters = [],
  setXCategoryFilters,
  vizTopN,
  setVizTopN,
  vizSortOrder,
  setVizSortOrder,
  topNOptions = [],
  sortOptions = [],
  vizMapping,
  setVizMapping,
  findColumn,
  getEffectiveColumnKey,
  handleDropField,
  allowDrop,
  vizChartPayload,
  isVizChartLoading,
  vizChartError,
  vizChartResetKey = 0,
}) => (
  <div className="m-4 bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200 shadow-lg overflow-hidden">
    {/* Header with gradient accent */}
    <div className="bg-gradient-to-r from-[#144677] to-[#1a5c99] p-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-white/10 rounded-lg backdrop-blur-sm">
          <FontAwesomeIcon icon={faChartBar} className="w-5 h-5 text-white" />
        </div>
        <h2 className="text-xl font-bold text-white">
          Visualization Builder (Drag & Drop)
        </h2>
      </div>
      <p className="text-sm text-white/80 pl-11">
        Drag columns into the X/Y/Series/Range areas to automatically
        generate the chart based on your selection.
      </p>
    </div>

    {/* Controls Section */}
    <div className="p-6 bg-white border-b border-gray-100 overflow-visible">
      <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
        {showTableSelectors && (
          <>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-semibold text-[#144677] mb-2">
                Left Table
              </label>
              <SelectDropdown
                options={vizTableOptions}
                isMulti={false}
                isClearable={false}
                placeholder="Select table"
                onChange={(value) =>
                  setVizLeftTable(value?.value || "Consignments")
                }
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-semibold text-[#144677] mb-2">
                Right Table (Optional)
              </label>
              <SelectDropdown
                options={vizTableOptions.filter(
                  (option) => option.value !== vizLeftTable
                )}
                isMulti={false}
                isClearable={true}
                placeholder="None"
                onChange={(value) => setVizRightTable(value?.value || "")}
              />
            </div>
            <div className="flex-1 min-w-[180px]">
              <label className="block text-sm font-semibold text-[#144677] mb-2">
                Join Key (Left Join)
              </label>
              <SelectDropdown
                options={vizJoinOptions}
                isMulti={false}
                isClearable={false}
                placeholder="Select join key"
                isDisabled={!vizRightTable}
                onChange={(value) => setVizJoinKey(value?.value || "")}
              />
            </div>
          </>
        )}
      </div>
    </div>

    {/* Main Content */}
    <div className="p-6 overflow-visible">
      <div className="grid gap-6 lg:grid-cols-1">
        {/* Left Panel - Columns & Mapping */}
        <div className="space-y-4">
          {/* Available Columns */}
          <div className="rounded-xl border-2 border-dashed border-gray-300 p-5 bg-white">
            <p className="text-sm font-bold text-[#144677] mb-3">
              Available Columns
            </p>
            {isVizDataLoading && (
              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-4 border-2 border-[#144677] border-t-transparent rounded-full animate-spin"></div>
                <p className="text-xs text-gray-500">
                  Loading visualization data...
                </p>
              </div>
            )}
            {vizDataError && (
              <div className="flex items-center gap-2 mb-3 p-2 bg-red-50 rounded-lg">
                <FontAwesomeIcon icon={faExclamationCircle} className="w-4 h-4 text-[#FF6266]" />
                <p className="text-xs text-[#FF6266]">{vizDataError}</p>
              </div>
            )}
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {Array.isArray(availableColumns) && availableColumns.length > 0 ? (
                <div className="space-y-3 px-1 pb-1">
                  {CATEGORY_ORDER.map((category) => {
                    const columns = availableColumns.filter(
                      (column) => (column.category || "Other") === category
                    );
                    if (!columns.length) {
                      return null;
                    }
                    return (
                      <div key={category} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <p className="mb-2 text-[11px] font-bold uppercase tracking-wide text-[#144677]">
                          {category}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {columns.map((column) => (
                            <span
                              key={column.key}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 cursor-grab hover:from-[#144677] hover:to-[#1a5c99] hover:text-white transition-all duration-200"
                              draggable
                              onDragStart={(event) => {
                                event.dataTransfer.setData(
                                  "text/plain",
                                  column.key
                                );
                              }}
                            >
                              {column.label} · {column.type}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                visualizationTables.map((table) => {
                  const isOpen = vizTableToggles[table.name];
                  return (
                    <div
                      key={table.name}
                      className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden"
                    >
                      <button
                        type="button"
                        className="w-full px-4 py-2.5 text-left text-xs font-bold text-[#144677] flex items-center justify-between hover:bg-gray-50 transition-colors"
                        onClick={() =>
                          setVizTableToggles((prev) => ({
                            ...prev,
                            [table.name]: !prev[table.name],
                          }))
                        }
                      >
                        <span>{table.name}</span>
                        <span className="text-gray-400 text-xs">
                          {isOpen ? "Hide" : "Show"}
                        </span>
                      </button>
                      {isOpen && (
                        <div className="px-3 pb-3 pt-1 flex flex-wrap gap-2 bg-gray-50">
                          {table.columns.map((column) => (
                            <span
                              key={`${table.name}-${column.key}`}
                              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 cursor-grab hover:from-[#144677] hover:to-[#1a5c99] hover:text-white transition-all duration-200"
                              draggable
                              onDragStart={(event) => {
                                event.dataTransfer.setData(
                                  "text/plain",
                                  getEffectiveColumnKey
                                    ? getEffectiveColumnKey(
                                        table.name,
                                        column.key
                                      )
                                    : column.key
                                );
                              }}
                            >
                              {column.label} · {column.type}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Drop Zones */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div
              className="rounded-xl border-2 border-dashed border-[#144677]/30 p-4 min-h-[80px] bg-white hover:border-[#144677] hover:bg-[#144677]/5 transition-all duration-200"
              onDrop={handleDropField("xField")}
              onDragOver={allowDrop}
            >
              <p className="text-xs font-bold text-[#144677] mb-2">
                X Axis / Category
              </p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-gray-700 font-medium">
                  {vizMapping.xField
                    ? findColumn(vizMapping.xField)?.label
                    : "Drop column here"}
                </p>
                {vizMapping.xField && setVizMapping && (
                  <button
                    type="button"
                    className="p-1 rounded-full text-gray-400 hover:text-[#FF6266] hover:bg-red-50 transition-colors"
                    onClick={() =>
                      setVizMapping((prev) => ({ ...prev, xField: null }))
                    }
                    aria-label="Clear X Axis"
                  >
                    <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            <div
              className="rounded-xl border-2 border-dashed border-[#144677]/30 p-4 min-h-[80px] bg-white hover:border-[#144677] hover:bg-[#144677]/5 transition-all duration-200"
              onDrop={handleDropField("yField")}
              onDragOver={allowDrop}
            >
              <p className="text-xs font-bold text-[#144677] mb-2">
                Y Axis / Value
              </p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-gray-700 font-medium">
                  {vizMapping.yField
                    ? findColumn(vizMapping.yField)?.label
                    : "Drop column here"}
                </p>
                {vizMapping.yField && setVizMapping && (
                  <button
                    type="button"
                    className="p-1 rounded-full text-gray-400 hover:text-[#FF6266] hover:bg-red-50 transition-colors"
                    onClick={() =>
                      setVizMapping((prev) => ({ ...prev, yField: null }))
                    }
                    aria-label="Clear Y Axis"
                  >
                    <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
            <div
              className="rounded-xl border-2 border-dashed border-[#144677]/30 p-4 min-h-[80px] bg-white hover:border-[#144677] hover:bg-[#144677]/5 transition-all duration-200 sm:col-span-2"
              onDrop={handleDropField("seriesField")}
              onDragOver={allowDrop}
            >
              <p className="text-xs font-bold text-[#144677] mb-2">
                Series / Group
              </p>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-gray-700 font-medium">
                  {vizMapping.seriesField
                    ? findColumn(vizMapping.seriesField)?.label
                    : "Optional"}
                </p>
                {vizMapping.seriesField && setVizMapping && (
                  <button
                    type="button"
                    className="p-1 rounded-full text-gray-400 hover:text-[#FF6266] hover:bg-red-50 transition-colors"
                    onClick={() =>
                      setVizMapping((prev) => ({ ...prev, seriesField: null }))
                    }
                    aria-label="Clear Series"
                  >
                    <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* X Category Filter */}
          {xCategoryOptions.length > 0 && (
            <div className="overflow-visible">
              <label className="block text-sm font-semibold text-[#144677] mb-2">
                Filter X Categories
              </label>
              <SelectDropdown
                options={xCategoryOptions}
                isMulti={true}
                placeholder="All categories"
                value={xCategoryFilters}
                onChange={(value) =>
                  setXCategoryFilters ? setXCategoryFilters(value || []) : null
                }
              />
            </div>
          )}

          {/* Chart Controls */}
          <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-semibold text-[#144677] mb-2">
                Chart Type
              </label>
              <SelectDropdown
                options={vizChartOptions}
                isMulti={false}
                isClearable={false}
                placeholder="Select chart type"
                value={
                  vizChartOptions.find((option) => option.value === vizChartType) ||
                  null
                }
                onChange={(value) => setVizChartType(value?.value || "line")}
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-semibold text-[#144677] mb-2">
                Aggregate
              </label>
              <SelectDropdown
                options={vizAggregateOptions}
                isMulti={false}
                isClearable={false}
                placeholder="SUM"
                value={
                  vizAggregateOptions.find((option) => option.value === vizAggregate) ||
                  null
                }
                onChange={(value) =>
                  setVizAggregate ? setVizAggregate(value?.value || "SUM") : null
                }
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-semibold text-[#144677] mb-2">
                Order by
              </label>
              <SelectDropdown
                options={sortOptions}
                isMulti={false}
                isClearable={false}
                placeholder="Largest first"
                value={
                  sortOptions.find((option) => option.value === vizSortOrder) ||
                  null
                }
                onChange={(value) =>
                  setVizSortOrder ? setVizSortOrder(value?.value || "desc") : null
                }
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-semibold text-[#144677] mb-2">
                Top N
              </label>
              <SelectDropdown
                options={topNOptions}
                isMulti={false}
                isClearable={false}
                placeholder="Top 10"
                value={
                  topNOptions.find((option) => option.value === vizTopN) || null
                }
                onChange={(value) =>
                  setVizTopN ? setVizTopN(value?.value ?? 10) : null
                }
              />
            </div>
            <button
              type="button"
              className="px-4 py-2.5 text-sm font-semibold rounded-lg bg-gradient-to-r from-[#FF6266] to-[#ff7a7d] text-white hover:shadow-lg hover:scale-105 transition-all duration-200 flex items-center gap-2 justify-center"
              onClick={clearVizMapping}
            >
              <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
              Clear Mapping
            </button>
          </div>

          {/* Tips */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 border-l-4 border-[#144677] rounded-lg">
            <FontAwesomeIcon icon={faLightbulb} className="w-4 h-4 text-[#144677] mt-0.5 flex-shrink-0" />
            <p className="text-xs text-gray-700">
              <span className="font-semibold">Tips:</span> Use numeric columns for Y. Scatter/Bubble need numeric X and Y.
              Candlestick uses open/high/low/close. Boxplot uses min/max.
            </p>
          </div>
        </div>

        {/* Preview */}
        <div className="rounded-xl border border-gray-200 p-5 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-sm font-bold text-[#144677]">Preview</p>
            <button
              type="button"
              className={`px-3 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#144677] to-[#1a5c99] text-white hover:shadow-lg hover:scale-105 transition-all duration-200 flex items-center gap-2 ${
                !vizChartPayload.isValid || isVizChartLoading
                  ? "opacity-50 cursor-not-allowed hover:scale-100"
                  : ""
              }`}
              onClick={() => {
                if (!vizChartPayload.isValid || isVizChartLoading) {
                  return;
                }
                ApexCharts.exec("visualization-lab", "dataURI").then(
                  ({ imgURI }) => {
                    const link = document.createElement("a");
                    link.href = imgURI;
                    link.download = "visualization-chart.png";
                    link.click();
                  }
                );
              }}
            >
              <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
              Download PNG
            </button>
          </div>
          {isVizChartLoading && (
            <div className="flex items-center justify-center py-20">
              <div className="flex flex-col items-center gap-3">
                <div className="w-12 h-12 border-4 border-[#144677] border-t-transparent rounded-full animate-spin"></div>
                <p className="text-sm font-medium text-gray-600">
                  Loading chart data...
                </p>
              </div>
            </div>
          )}
          {vizChartError && (
            <div className="bg-red-50 border-l-4 border-[#FF6266] p-4 rounded-lg">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faExclamationCircle} className="w-5 h-5 text-[#FF6266]" />
                <p className="text-sm font-medium text-red-800">{vizChartError}</p>
              </div>
            </div>
          )}
          {!isVizChartLoading && !vizChartError && !vizChartPayload.isValid && (
            <div className="bg-red-50 border-l-4 border-[#FF6266] p-4 rounded-lg">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faExclamationCircle} className="w-5 h-5 text-[#FF6266]" />
                <p className="text-sm font-medium text-red-800">{vizChartPayload.reason}</p>
              </div>
            </div>
          )}
          {!isVizChartLoading && !vizChartError && vizChartPayload.isValid && (
            <div className="bg-gray-50 rounded-lg p-4">
              <Chart
                key={`${vizChartResetKey}-${vizChartPayload.chart.options.chart.type}`}
                options={vizChartPayload.chart.options}
                series={vizChartPayload.chart.series}
                type={vizChartPayload.chart.options.chart.type}
                height={440}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

export default VisualizationBuilder;
